import React, { useRef, useState, useEffect, useLayoutEffect, useMemo, useCallback } from 'react';
import { useGameState } from '../../net/GameProvider.js';
import { isCardGroupStart } from '../../utils/handGrouping.js';
import { suitOf } from '@big2/shared';
import { CardUI } from '../CardUI.js';

interface MyHandProps {
  isPaused: boolean;
  sortedHand: string[];
  selectedCards: Set<string>;
  toggleCard: (card: string) => void;
  onReorder?: (order: string[]) => void;
  onSelectMultiple?: (cards: string[], mode?: 'add' | 'remove' | 'toggle' | 'replace') => void;
  arrangeControls?: React.ReactNode;
  isMyTurn?: boolean;
  sortBy?: 'rank' | 'suit' | 'pairs' | string;
  sortVersion?: number;
  trumpSuit?: string;
  playableCards?: Set<string>;
  dimUnplayable?: boolean;
  selectionDisabled?: boolean;
  emptyStateMessage?: string;
}

export function MyHand({
  isPaused,
  sortedHand,
  selectedCards,
  toggleCard,
  onReorder,
  arrangeControls,
  isMyTurn,
  sortBy,
  sortVersion,
  trumpSuit,
  playableCards,
  dimUnplayable,
  selectionDisabled,
  emptyStateMessage,
}: MyHandProps) {
  const { state } = useGameState();
  const { game } = state;
  const rootRef = useRef<HTMLDivElement | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const items = useRef(new Map<string, HTMLElement>());
  const beforeMove = useRef(new Map<string, DOMRect>());

  const [displayCards, setDisplayCards] = useState<string[]>(sortedHand);
  const [drawnCards, setDrawnCards] = useState<Set<string>>(new Set());
  const drawnTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [scrollRatio, setScrollRatio] = useState({ canScroll: false, thumbLeft: 0, thumbWidth: 100 });
  const [containerWidth, setContainerWidth] = useState<number>(0);

  // Sync displayCards when sortedHand or sort mode changes externally
  const prevSortedHandRef = useRef(sortedHand);
  const prevSortByRef = useRef(sortBy);
  const prevSortVersionRef = useRef(sortVersion);

  useEffect(() => {
    const sortByChanged = sortBy !== prevSortByRef.current;
    const sortVersionChanged = sortVersion !== undefined && sortVersion !== prevSortVersionRef.current;
    prevSortByRef.current = sortBy;
    prevSortVersionRef.current = sortVersion;

    // If sort button was clicked or external sort mode changed, adopt sortedHand immediately
    if (sortByChanged || sortVersionChanged) {
      setDisplayCards((prev) => {
        if (prev.join(',') !== sortedHand.join(',')) {
          beforeMove.current = new Map(
            [...items.current].map(([id, element]) => [id, element.getBoundingClientRect()])
          );
          return [...sortedHand];
        }
        beforeMove.current.clear();
        return prev;
      });
      prevSortedHandRef.current = sortedHand;
      return;
    }

    // Check if card composition changed (cards played or dealt)
    const prevHand = prevSortedHandRef.current;
    if (prevHand.join(',') !== sortedHand.join(',')) {
      const added = sortedHand.filter((c) => !prevHand.includes(c));
      if (added.length > 0) {
        setDrawnCards(new Set(added));
        if (drawnTimerRef.current) clearTimeout(drawnTimerRef.current);
        drawnTimerRef.current = setTimeout(() => {
          setDrawnCards(new Set());
        }, added.length * 45 + 380);
      }
      prevSortedHandRef.current = sortedHand;
      setDisplayCards((prev) => {
        // Only preserve manual relative order when cards are removed (cards played during trick)
        if (added.length === 0 && sortedHand.length < prevHand.length) {
          const remaining = prev.filter((c) => sortedHand.includes(c));
          if (remaining.length === sortedHand.length) {
            return remaining;
          }
        }
        // When cards are dealt (new game, round start, or deal), always adopt the clean sorted hand
        return [...sortedHand];
      });
    }
  }, [sortedHand, sortBy, sortVersion]);

  // Cleanup draw animation timer
  useEffect(() => {
    return () => {
      if (drawnTimerRef.current) clearTimeout(drawnTimerRef.current);
    };
  }, []);

  if (!game) return null;

  const myPlayerObj = game.players.find((p) => p.playerId === game.myPlayerId);
  const cardCount = isPaused ? (myPlayerObj?.cardCount ?? displayCards.length) : displayCards.length;

  const updateScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setContainerWidth(el.clientWidth);
    const isScrollable = el.scrollWidth > el.clientWidth + 4;
    if (!isScrollable) {
      setScrollRatio({ canScroll: false, thumbLeft: 0, thumbWidth: 100 });
      return;
    }
    const ratio = el.clientWidth / el.scrollWidth;
    const maxScroll = el.scrollWidth - el.clientWidth;
    const scrollLeftRatio = maxScroll > 0 ? el.scrollLeft / maxScroll : 0;
    const thumbWidth = Math.max(ratio * 100, 20);
    const maxLeft = 100 - thumbWidth;
    const thumbLeft = Math.min(Math.max(scrollLeftRatio * maxLeft, 0), maxLeft);
    setScrollRatio({ canScroll: true, thumbLeft, thumbWidth });
  }, []);

  useEffect(() => {
    updateScroll();
    const el = scrollRef.current;
    if (!el) return;
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => updateScroll()) : null;
    ro?.observe(el);
    window.addEventListener('resize', updateScroll);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', updateScroll);
    };
  }, [cardCount, isPaused, updateScroll]);

  // FLIP animation on manual reorder, identical to DaaluuHand
  useLayoutEffect(() => {
    if (!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      for (const [id, oldRect] of beforeMove.current) {
        const element = items.current.get(id);
        if (!element) continue;
        const rect = element.getBoundingClientRect();
        const x = oldRect.left - rect.left;
        if (x) {
          element.animate?.(
            [{ transform: `translateX(${x}px)` }, { transform: 'translateX(0)' }],
            { duration: 150, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }
          );
        }
      }
    }
    beforeMove.current.clear();
  }, [displayCards.join(',')]);

  // Dynamic responsive accordion overlap: fits all cards on screen
  const dynamicSpacing = useMemo(() => {
    if (!containerWidth || cardCount <= 1) return null;
    const isMobile = containerWidth < 600;
    const isLarge = containerWidth >= 1024;
    const baseCardWidth = isMobile ? 68 : isLarge ? 90 : 72;
    const defaultOverlap = isMobile ? 22 : isLarge ? 32 : 22;
    const naturalTotalWidth = baseCardWidth + (cardCount - 1) * (baseCardWidth - defaultOverlap);
    const availableWidth = containerWidth - 16;

    if (naturalTotalWidth <= availableWidth) {
      return {
        overlapPx: defaultOverlap,
        canFitAll: true,
      };
    }

    const calculatedStep = (availableWidth - baseCardWidth) / (cardCount - 1);
    const minStep = isMobile ? 32 : 26;

    if (calculatedStep >= minStep) {
      const adaptiveOverlap = baseCardWidth - calculatedStep;
      return {
        overlapPx: Math.round(adaptiveOverlap),
        canFitAll: true,
      };
    }

    return {
      overlapPx: baseCardWidth - minStep,
      canFitAll: false,
    };
  }, [containerWidth, cardCount]);

  function moveOrder(next: string[]) {
    beforeMove.current = new Map(
      [...items.current].map(([id, element]) => [id, element.getBoundingClientRect()])
    );
    setDisplayCards(next);
    onReorder?.(next);
  }

  const handleCardClick = (card: string) => {
    if (selectionDisabled || isPaused) return;
    toggleCard(card);
  };

  const handleTrackClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = scrollRef.current;
    const track = e.currentTarget;
    if (!el || !track) return;
    const rect = track.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickRatio = Math.max(0, Math.min(1, clickX / rect.width));
    const maxScroll = el.scrollWidth - el.clientWidth;
    el.scrollTo({ left: clickRatio * maxScroll, behavior: 'smooth' });
  };

  return (
    <>
      <div className={`hand-area-row ${scrollRatio.canScroll ? 'is-scrollable' : 'is-compact'} ${isMyTurn ? 'is-my-turn' : 'is-waiting-turn'}`}>
        <div className="hand-column">
          <div
            className="hand-scroll-container"
            ref={scrollRef}
            onScroll={updateScroll}
          >
            {/* --n lets CSS fit every card on one row (overlap computed from container width). */}
            <div
              ref={rootRef}
              className="hand-cards-row"
              style={{
                '--n': Math.max(cardCount, 1),
                ...(dynamicSpacing ? { '--dynamic-overlap': `-${dynamicSpacing.overlapPx}px` } : {}),
              } as React.CSSProperties}
            >
              {isPaused
                ? Array.from({ length: cardCount }, (_, idx) => (
                    <CardUI
                      key={`paused-card-${idx}`}
                      faceDown
                      style={{ zIndex: idx + 1 }}
                      className={idx > 0 ? 'hand-card-item hand-card-overlap' : 'hand-card-item'}
                    />
                  ))
                : displayCards.length === 0 && emptyStateMessage
                ? (
                    <div className="hand-empty-notice" role="status">
                      <span className="hand-empty-icon" aria-hidden="true">🛡️</span>
                      <span className="hand-empty-text">{emptyStateMessage}</span>
                    </div>
                  )
                : displayCards.map((c, idx) => {
                    const isOpening = !isPaused && game.gameType !== 'muushig' && game.gameType !== 'twentyone' && game.mustInclude === c;
                    const isNewGroup = idx > 0 && isCardGroupStart(displayCards[idx - 1], c, displayCards, sortBy, trumpSuit);
                    const isTrump = Boolean(trumpSuit && suitOf(c) === trumpSuit);
                    const isPlayable = isMyTurn && !isPaused && Boolean(playableCards && playableCards.has(c));
                    const isDimmed = !isPaused && Boolean(dimUnplayable && isMyTurn && playableCards && !playableCards.has(c));
                    const isDrawn = drawnCards.has(c);
                    const drawnIdx = isDrawn ? Array.from(drawnCards).indexOf(c) : 0;

                    const overlapClass = idx === 0
                      ? 'hand-card-wrapper'
                      : isNewGroup
                      ? 'hand-card-wrapper hand-card-group-start'
                      : 'hand-card-wrapper hand-card-overlap';

                    return (
                      <span
                        key={c}
                        ref={(el) => {
                          if (el) items.current.set(c, el);
                          else items.current.delete(c);
                        }}
                        data-card-item={c}
                        className={`${overlapClass}${isDrawn ? ' is-drawn-card' : ''}`}
                        style={{
                          zIndex: idx + 1,
                          ...(isDrawn ? ({ '--draw-idx': drawnIdx } as React.CSSProperties) : {}),
                        }}
                        onKeyDownCapture={(e) => {
                          if (!e.altKey || !['ArrowLeft', 'ArrowRight'].includes(e.key)) return;
                          e.preventDefault();
                          const index = displayCards.indexOf(c);
                          const target = index + (e.key === 'ArrowLeft' ? -1 : 1);
                          if (target < 0 || target >= displayCards.length) return;
                          const next = [...displayCards];
                          next.splice(index, 1);
                          next.splice(target, 0, c);
                          moveOrder(next);
                        }}
                      >
                        <CardUI
                          cardCode={c}
                          selected={selectedCards.has(c)}
                          isOpeningCard={isOpening}
                          isPlayable={isPlayable}
                          isDimmed={isDimmed}
                          isTrump={isTrump}
                          className="hand-card-item"
                          onClick={() => (!selectionDisabled && !isDimmed ? handleCardClick(c) : undefined)}
                        />
                      </span>
                    );
                  })}
            </div>
          </div>
          <div
            className={`hand-scrollbar-slot ${scrollRatio.canScroll ? 'can-scroll' : ''}`}
            aria-hidden={!scrollRatio.canScroll}
          >
            <div
              className="hand-scrollbar-track"
              role="scrollbar"
              aria-label="Hand cards scroll"
              aria-valuenow={Math.round(scrollRatio.thumbLeft)}
              aria-valuemin={0}
              aria-valuemax={100}
              onClick={scrollRatio.canScroll ? handleTrackClick : undefined}
              style={{ visibility: scrollRatio.canScroll ? 'visible' : 'hidden', pointerEvents: scrollRatio.canScroll ? 'auto' : 'none' }}
            >
              <div
                className="hand-scrollbar-thumb"
                style={{
                  left: `${scrollRatio.thumbLeft}%`,
                  width: `${scrollRatio.thumbWidth}%`,
                }}
              />
            </div>
          </div>
        </div>
        {arrangeControls}
      </div>
    </>
  );
}
